import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { getProducts } from '../../../../api/product/queries/getProducts';
import { Icon } from '../../../../components/common/icon/Icon';
import { TIRE_CONDITION_OPTIONS, TireConditionType } from '../../../../constants/product/product.constant';
import { useGetHomeImagesQuery } from '../../../../hooks/api/home/queries/useGetHomeImages.query';
import { IOpenMarketProductDetail, IProduct } from '../../../../interfaces/Product.interfaces';
import { MainCarousel } from '../../../../pages/Home/HomePage/components/MainCarousel';
import { imageTypes } from '../../../../store/userSellTractorSurveySlice';
import { formatDateFromTodayOnlyDifference } from '../../../../utils/formatter/formatDateFromTodayOnlyDifference.util';
import { formatPrice } from '../../../../utils/formatter/formatPrice.util';
import { getOpenMarketProductTitle } from '../../../../utils/getter/getOpenMarketProductTitle.util';
import { getShortAddress } from '../../../../utils/getter/getShortAddress.util';
import { ProductHorizontalListSummaryViewGroupWrapper } from '../../listView/ProductHorizontalListSummaryView/ProductHorizontalListSummaryViewGroupWrapper';
import { OptionChip, OptionChipSection, OptionChipText } from '../components/OptionChipSection';
import { ProductImageSection } from '../components/ProductImageSection';
import { ProductSpecSummarySection } from '../components/ProductSpecSummarySection';

interface OpenMarketProductSummaryViewProps {
  product: IOpenMarketProductDetail;
}

const EmptyMessage = ({ message }: { message: string }) => {
  return <EmptyMessageWrapper>{message}</EmptyMessageWrapper>;
};

export const OpenMarketProductSummaryView = ({ product }: OpenMarketProductSummaryViewProps) => {
  // TODO: 스펙 확정나면 useGetProductsQuery 사용하도록 변경
  const navigate = useNavigate();

  const MAX_PRODUCTS_COUNT = 5;
  const pluvaProductsParam = {
    category: 'PLUVA',
    status: 'ONSALE',
    count: MAX_PRODUCTS_COUNT,
  };
  const partnerProductsParam = {
    category: 'PARTNER',
    status: 'ONSALE',
    count: MAX_PRODUCTS_COUNT,
  };
  const { data: pluvaProducts } = useQuery<IProduct[], AxiosError>(
    ['products', `category=${pluvaProductsParam.category}`, `status=${pluvaProductsParam.status}`],
    () =>
      getProducts({
        searchParam: pluvaProductsParam,
      }),
  );

  const { data: partnerProducts } = useQuery<IProduct[], AxiosError>(
    ['products', `category=${partnerProductsParam.category}`, `status=${partnerProductsParam.status}`],
    () =>
      getProducts({
        searchParam: partnerProductsParam,
      }),
  );

  const { data: mainCarouselImages } = useGetHomeImagesQuery({
    searchParam: {
      category: 'CAROUSEL_MAIN',
    },
  });

  const getTireConditionDescription = (
    frontTireCondition?: TireConditionType,
    rearTireCondition?: TireConditionType,
  ): string => {
    const frontTireLabel = '앞 타이어';
    const rearTireLabel = '뒷 타이어';

    const frontTireDescription = frontTireCondition
      ? `${frontTireLabel} ${mapTireConditionToText(frontTireCondition)}`
      : '';

    const rearTireDescription = rearTireCondition
      ? `${rearTireLabel} ${mapTireConditionToText(rearTireCondition)}`
      : '';

    const descriptions = [frontTireDescription, rearTireDescription].filter(Boolean);
    return descriptions.length > 0 ? `${descriptions.join(', ')} 👨🏻‍🔧` : '';
  };

  const mapTireConditionToText = (condition: TireConditionType): string => {
    switch (condition) {
      case TIRE_CONDITION_OPTIONS.High:
        return '80% 이상';
      case TIRE_CONDITION_OPTIONS.Medium:
        return '50% 가량 남음';
      case TIRE_CONDITION_OPTIONS.Low:
        return '50% 이하 남음';
      default:
        return '';
    }
  };

  const hasReplacedParts = product && product.replacedParts && product.replacedParts.length > 0;
  const hasNeedRepairParts = product && product.needRepairParts && product.needRepairParts.length > 0;
  const hasReplacedOrNeedRepairParts = hasReplacedParts || hasNeedRepairParts;

  const tireConditionDescription = getTireConditionDescription(product?.frontTireCondition, product?.rearTireCondition);
  const hasEtcFeatures = tireConditionDescription || product?.directImportYn || product?.priceNegotiationYn;

  const sortedImages = product?.images?.sort((a, b) => {
    const indexA = imageTypes.indexOf(a.type as (typeof imageTypes)[number]);
    const indexB = imageTypes.indexOf(b.type as (typeof imageTypes)[number]);
    return indexA - indexB;
  });

  if (product) {
    return (
      <>
        <ProductImageSection images={sortedImages} status={product.status} />

        <ProductInfoContainer>
          <Section1>
            <ProductInfoSummaryContainer>
              <ProductInfoSummary>{product.machine.brand.name}</ProductInfoSummary>
              <ProductInfoSummary>{'·'}</ProductInfoSummary>
              {product.approvalDate ? (
                <>
                  <ProductInfoSummary>{`${formatDateFromTodayOnlyDifference(
                    product.approvalDate,
                  )} 등록`}</ProductInfoSummary>
                  <ProductInfoSummary>{'·'}</ProductInfoSummary>
                </>
              ) : null}
              <ProductInfoSummary>{`조회 ${product.views}`}</ProductInfoSummary>
              <ProductInfoSummary>{'·'}</ProductInfoSummary>
              <ProductInfoSummary>{getShortAddress(product.location)}</ProductInfoSummary>
            </ProductInfoSummaryContainer>

            <TitlePriceContainer>
              <Title>{getOpenMarketProductTitle(product)}</Title>
              <Price>{`${formatPrice(product.hopeSellingPrice)}원`}</Price>
            </TitlePriceContainer>
          </Section1>

          <Section2>
            <ProductSpecSummarySection
              manufacturedYear={product.manufacturedYear}
              operationTime={product.operationTime}
              engineOutputInPs={product.machine.engineOutputInPs}
              isGrayscale
            />
          </Section2>

          <HorizontalLine />

          <Section3>
            <SectionTitle>{'매물 옵션'}</SectionTitle>
            {product.optionDetails && product.optionDetails.length > 0 ? (
              <OptionChipSection>
                {product.optionDetails.map((option, index) => (
                  <OptionChip key={option}>
                    <OptionChipText>{option}</OptionChipText>
                  </OptionChip>
                ))}
              </OptionChipSection>
            ) : (
              <EmptyMessage message={'없음'} />
            )}
          </Section3>

          <HorizontalLine />

          <Section4>
            <SectionTitle>{'작업기 정보'}</SectionTitle>
            {product.workingMachineDetails && product.workingMachineDetails.length > 0 ? (
              <ProductFeaturesContaier>
                {product.workingMachineDetails.map((workingMachine) => (
                  <ProductFeatureWrapper key={workingMachine}>
                    <ProductFeatureText>{workingMachine}</ProductFeatureText>
                  </ProductFeatureWrapper>
                ))}
              </ProductFeaturesContaier>
            ) : (
              <EmptyMessage message={'없음'} />
            )}
          </Section4>

          <HorizontalLine />

          <Section5>
            <SectionTitle>{'수리가 필요하거나 교체한 부품'}</SectionTitle>
            {hasReplacedOrNeedRepairParts ? (
              <>
                {hasNeedRepairParts && (
                  <ProductPartsContaier>
                    <ProductPartsTitleSection>
                      <Icon name={'ExclamationMarkRed'} width={20} height={20} />
                      <ProductPartsTitle>{'수리가 필요한 항목'}</ProductPartsTitle>
                    </ProductPartsTitleSection>
                    <ProductPartsDescription>
                      {product.needRepairParts.map((needRepairPart) => (
                        <ProductPartDescriptionText
                          key={needRepairPart}
                        >{`• ${needRepairPart}`}</ProductPartDescriptionText>
                      ))}
                    </ProductPartsDescription>
                  </ProductPartsContaier>
                )}
                {hasReplacedParts && (
                  <ProductPartsContaier>
                    <ProductPartsTitleSection>
                      <Icon name={'ArrowUturnRightGreen'} width={20} height={20} />
                      <ProductPartsTitle>{'교체한 부품 내역'}</ProductPartsTitle>
                    </ProductPartsTitleSection>
                    <ProductPartsDescription>
                      {product.replacedParts.map((replacedPart) => (
                        <ProductPartDescriptionText
                          key={replacedPart}
                        >{`• ${replacedPart}`}</ProductPartDescriptionText>
                      ))}
                    </ProductPartsDescription>
                  </ProductPartsContaier>
                )}
              </>
            ) : (
              <EmptyMessage message={'없음'} />
            )}
          </Section5>
          <Section6>
            <SectionTitle>{'판매자의 매물 소개'}</SectionTitle>
            {!hasEtcFeatures && !product.information && <EmptyMessage message={'없음'} />}
            {hasEtcFeatures && (
              <EtcDetailsSection>
                {tireConditionDescription && (
                  <ProductFeaturesContaier>
                    <ProductFeatureWrapper>
                      <ProductFeatureText>{tireConditionDescription}</ProductFeatureText>
                    </ProductFeatureWrapper>
                  </ProductFeaturesContaier>
                )}
                {product.directImportYn && (
                  <ProductFeaturesContaier>
                    <ProductFeatureWrapper>
                      <ProductFeatureText>{'해외 직수입 매물 🌟'}</ProductFeatureText>
                    </ProductFeatureWrapper>
                  </ProductFeaturesContaier>
                )}
                {product.priceNegotiationYn && (
                  <ProductFeaturesContaier>
                    <ProductFeatureWrapper>
                      <ProductFeatureText>{'가격 협상 가능 💸'}</ProductFeatureText>
                    </ProductFeatureWrapper>
                  </ProductFeaturesContaier>
                )}
              </EtcDetailsSection>
            )}
            {product.information && <ProductInfomationText>{product.information}</ProductInfomationText>}
          </Section6>
        </ProductInfoContainer>

        {mainCarouselImages && mainCarouselImages.length > 0 && (
          <MainCarousel carouselImages={mainCarouselImages} pageType={'pdp'} />
        )}

        <Description>
          <DescriptionTitle>{'고객간 거래에 대하여'}</DescriptionTitle>
          <DescriptionContent>
            {
              '플루바 마켓은 통신판매중개자이며, 통신판매의 당사자가 아닙니다. 전자상거래 등에서의 소비자보호에 관한 법률 등 관련 법령 및 플루바 마켓의 약관에 따라 상품, 상품정보, 거래에 관한 책임은 개별 판매자에게 귀속하고, 플루바 마켓은 원칙적으로 회원간 거래에 대하여 책임을 지지 않습니다. 다만, 플루바 마켓이 직접 판매하는 상품에 대한 책임은 플루바 마켓에게 귀속합니다.'
            }
          </DescriptionContent>
        </Description>

        <ProductInfoContainer>
          {pluvaProducts && pluvaProducts.length >= 3 && (
            <>
              <Section6>
                <ProductHorizontalListSummaryViewGroupWrapper
                  products={pluvaProducts}
                  category={'pluva'}
                  pageType={'odp'}
                  currentProductForMarketAmpliEvent={{ category: 'OPEN_MARKET', code: product.code }}
                  title={'플루바 매물 모아보기'}
                />
              </Section6>
            </>
          )}
          {partnerProducts && partnerProducts.length >= 3 && (
            <>
              <HorizontalLine />
              <Section6>
                <ProductHorizontalListSummaryViewGroupWrapper
                  products={partnerProducts}
                  category={'partner'}
                  pageType={'odp'}
                  currentProductForMarketAmpliEvent={{ category: 'OPEN_MARKET', code: product.code }}
                  title={'파트너 매물 모아보기'}
                />
              </Section6>
            </>
          )}
        </ProductInfoContainer>
      </>
    );
  }
  return <></>;
};

const Section = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  padding: 32px var(--radius-large, 16px);
  gap: 24px;
`;

const Section1 = styled(Section)``;

const Section2 = styled(Section)`
  padding: 0px var(--radius-large, 16px) 40px var(--radius-large, 16px);
`;

const Section3 = styled(Section)``;

const Section4 = styled(Section)``;

const Section5 = styled(Section)``;

const Section6 = styled(Section)``;

const ProductInfoSummaryContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--radius-small, 4px);
`;

const ProductInfoSummary = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 11.9px */
`;

const TitlePriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  gap: var(--radius-large, 16px);
`;

const Title = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
`;

const Price = styled.span`
  color: var(--Light-Scheme-Color-primary-red, #ff3049);

  /* System/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 15.3px */
`;

const SectionTitle = styled.h3`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 15.3px */
`;

const ProductInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ProductFeaturesContaier = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;

  gap: var(--radius-medium, 8px);
`;

const ProductPartsContaier = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-medium, 8px);
  align-self: stretch;
`;

const ProductPartsTitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: var(--radius-small, 4px);
  align-self: stretch;
`;

const ProductPartsTitle = styled.h4`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 18.2px */
`;

const ProductPartsDescription = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const ProductPartDescriptionText = styled.li`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Body/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
`;

const EmptyMessageWrapper = styled.span`
  color: var(--Light-Scheme-Monochrome-grey70, #a7a9b6);
  text-align: center;

  /* System/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 13.6px */
`;

const ProductFeatureWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;

  padding: var(--radius-large, 16px);

  gap: var(--radius-medium, 8px);

  border-radius: var(--radius-large, 16px) var(--radius-large, 16px) var(--radius-large, 16px) var(--radius-small, 4px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const ProductFeatureText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 18.2px */
`;

const EtcDetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-medium, 8px);
  align-self: stretch;
`;

const ProductInfomationText = styled.p`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Body/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
`;

const Description = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;

  padding: 24px;
  gap: var(--radius-medium, 8px);

  border-radius: var(--radius-none, 0px);

  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const DescriptionTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* Body/Small/s-bold */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 18px */
`;

const DescriptionContent = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* Body/Small/s-regular */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const HorizontalLine = styled.hr`
  width: 100%;

  height: 0px;
  align-self: stretch;
  stroke-width: 1px;
  stroke: #e4e4ed;
`;
